import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Send, CheckCircle, ArrowRight, ChevronRight } from 'lucide-react';
import { supabase } from '../lib/supabase';
import { trackEvent } from '../lib/analytics';
import { useI18n } from '../lib/i18n';

const ColabModal = ({ isOpen, onClose, programName }) => {
  const [currentStep, setCurrentStep] = useState(0);
  const [answers, setAnswers] = useState({});
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const { t, lang } = useI18n();
  const pt = lang === 'pt';

  const steps = [
    {
      id: 'type',
      question: t('colab.question', 'Como você quer colaborar?'),
      options: [
        { value: 'parceiro', label: pt ? 'Quero ser parceiro' : 'I want to be a partner', emoji: '🤝' },
        { value: 'patrocinador', label: pt ? 'Quero patrocinar' : 'I want to sponsor', emoji: '💎' },
        { value: 'voluntario', label: pt ? 'Quero ser voluntário' : 'I want to volunteer', emoji: '✊' },
        { value: 'aluno', label: pt ? 'Quero ser aluno' : 'I want to enroll', emoji: '🎓' },
        { value: 'indicar', label: pt ? 'Quero indicar alguém' : 'I want to refer someone', emoji: '📬' },
        { value: 'outro', label: pt ? 'Outro' : 'Other', emoji: '💬' },
      ],
    },
    { id: 'name', question: t('colab.nome', 'Qual seu nome?'), placeholder: pt ? 'Nome completo' : 'Full name', type: 'text' },
    { id: 'email', question: t('colab.email', 'Qual seu e-mail?'), placeholder: pt ? 'seu@email.com' : 'your@email.com', type: 'email' },
    { id: 'phone', question: t('colab.phone', 'Telefone ou WhatsApp?'), placeholder: '(11) 99999-9999', type: 'tel', optional: true },
    { id: 'message', question: t('colab.message', 'Quer dizer mais alguma coisa?'), placeholder: pt ? 'Mensagem opcional...' : 'Optional message...', type: 'textarea', optional: true },
  ];

  // Reset on close
  useEffect(() => {
    if (!isOpen) {
      setTimeout(() => {
        setCurrentStep(0);
        setAnswers({});
        setSuccess(false);
      }, 300);
    }
  }, [isOpen]);

  const currentStepData = steps[currentStep];
  const isLastStep = currentStep === steps.length - 1;
  const progress = ((currentStep + 1) / steps.length) * 100;

  const handleOptionSelect = (value) => {
    setAnswers({ ...answers, type: value });
    setTimeout(() => setCurrentStep(1), 300);
  };

  const handleInputSubmit = (e) => {
    e?.preventDefault();
    const value = answers[currentStepData.id];
    if (!currentStepData.optional && !value) return;

    if (isLastStep) {
      handleFinalSubmit();
    } else {
      setCurrentStep((s) => s + 1);
    }
  };

  const handleFinalSubmit = async () => {
    setLoading(true);
    try {
      await supabase.from('collaborations').insert({
        name: answers.name || '',
        email: answers.email || '',
        phone: answers.phone || '',
        type: answers.type || 'outro',
        program: programName,
        message: answers.message || '',
      });
      trackEvent('lead_submit', {
        category: 'conversion',
        properties: { type: answers.type, program: programName },
      });
    } catch (err) {
      console.error('Error saving collaboration:', err);
    }
    setLoading(false);
    setSuccess(true);
    setTimeout(() => {
      onClose();
    }, 2500);
  };

  const handleSkip = () => {
    if (isLastStep) {
      handleFinalSubmit();
    } else {
      setCurrentStep((s) => s + 1);
    }
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          className="fixed inset-0 z-[200] flex items-end md:items-center justify-center"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.25 }}
        >
          {/* Backdrop */}
          <motion.div
            className="absolute inset-0 bg-black/70 backdrop-blur-sm"
            onClick={onClose}
          />

          {/* Modal */}
          <motion.div
            className="relative w-full md:max-w-md bg-black text-white overflow-hidden md:mx-4"
            initial={{ y: 60, opacity: 0, scale: 0.98 }}
            animate={{ y: 0, opacity: 1, scale: 1 }}
            exit={{ y: 40, opacity: 0, scale: 0.98 }}
            transition={{ type: 'spring', damping: 28, stiffness: 350 }}
          >
            {/* Progress bar */}
            <div className="h-[2px] bg-white/5">
              <motion.div
                className="h-full bg-[#FFC107]"
                animate={{ width: `${progress}%` }}
                transition={{ duration: 0.4, ease: 'easeOut' }}
              />
            </div>

            {/* Header */}
            <div className="flex items-center justify-between px-6 pt-5 pb-2">
              <div>
                <span className="font-manrope text-[10px] uppercase tracking-[0.2em] text-[#FFC107]">
                  {programName}
                </span>
              </div>
              <motion.button
                onClick={onClose}
                className="w-8 h-8 flex items-center justify-center text-white/30 hover:text-white transition-colors"
                whileTap={{ scale: 0.9 }}
              >
                <X size={18} />
              </motion.button>
            </div>

            {/* Content */}
            <div className="px-6 pb-8 min-h-[320px] flex flex-col">
              <AnimatePresence mode="wait">
                {success ? (
                  <motion.div
                    key="success"
                    className="flex-1 flex flex-col items-center justify-center text-center py-12"
                    initial={{ opacity: 0, scale: 0.9 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0 }}
                  >
                    <motion.div
                      initial={{ scale: 0 }}
                      animate={{ scale: 1 }}
                      transition={{ type: 'spring', stiffness: 200, damping: 15, delay: 0.1 }}
                    >
                      <CheckCircle size={56} className="text-[#FFC107] mb-6" />
                    </motion.div>
                    <h4 className="font-oswald text-2xl uppercase mb-2">{t('colab.success', 'Recebemos!')}</h4>
                    <p className="font-manrope text-white/40 text-sm">
                      {t('colab.success_sub', 'Nossa equipe entrará em contato em breve.')}
                    </p>
                  </motion.div>
                ) : (
                  <motion.div
                    key={currentStep}
                    className="flex-1 flex flex-col pt-4"
                    initial={{ opacity: 0, x: 30 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: -30 }}
                    transition={{ duration: 0.25 }}
                  >
                    {/* Step counter */}
                    <span className="font-manrope text-[10px] text-white/20 uppercase tracking-wider mb-3">
                      {currentStep + 1} / {steps.length}
                    </span>

                    {/* Question */}
                    <h3 className="font-oswald text-xl md:text-2xl uppercase mb-6 leading-tight">
                      {currentStepData.question}
                    </h3>

                    {/* Options (step 0) */}
                    {currentStepData.options && (
                      <div className="space-y-2 flex-1">
                        {currentStepData.options.map((opt) => (
                          <motion.button
                            key={opt.value}
                            onClick={() => handleOptionSelect(opt.value)}
                            className={`w-full text-left px-4 py-3 border transition-all flex items-center gap-3 group ${
                              answers.type === opt.value
                                ? 'border-[#FFC107] bg-[#FFC107]/10 text-white'
                                : 'border-white/10 hover:border-white/25 text-white/60 hover:text-white'
                            }`}
                            whileHover={{ x: 4 }}
                            whileTap={{ scale: 0.98 }}
                          >
                            <span className="text-lg">{opt.emoji}</span>
                            <span className="font-manrope text-sm flex-1">{opt.label}</span>
                            <ChevronRight size={14} className="opacity-0 group-hover:opacity-50 transition-opacity" />
                          </motion.button>
                        ))}
                      </div>
                    )}

                    {/* Text input */}
                    {currentStepData.type && currentStepData.type !== 'textarea' && (
                      <form onSubmit={handleInputSubmit} className="flex-1 flex flex-col">
                        <input
                          type={currentStepData.type}
                          value={answers[currentStepData.id] || ''}
                          onChange={(e) => setAnswers({ ...answers, [currentStepData.id]: e.target.value })}
                          placeholder={currentStepData.placeholder}
                          className="w-full bg-transparent border-b-2 border-white/15 focus:border-[#FFC107] py-4 outline-none font-manrope text-lg text-white placeholder:text-white/20 transition-colors"
                          autoFocus
                          required={!currentStepData.optional}
                        />
                        <div className="flex items-center justify-between mt-auto pt-8">
                          {currentStepData.optional && (
                            <button
                              type="button"
                              onClick={handleSkip}
                              className="font-manrope text-xs text-white/25 hover:text-white/50 transition-colors uppercase tracking-wider"
                            >
                              {t('colab.skip', 'Pular')}
                            </button>
                          )}
                          <motion.button
                            type="submit"
                            className="ml-auto inline-flex items-center gap-2 h-12 px-8 bg-[#FFC107] text-black font-bold text-sm uppercase tracking-wider hover:bg-white transition-colors btn-spring"
                            whileHover={{ scale: 1.02 }}
                            whileTap={{ scale: 0.97 }}
                          >
                            {isLastStep ? t('colab.send', 'Enviar') : t('colab.continue', 'Continuar')}
                            <ArrowRight size={16} />
                          </motion.button>
                        </div>
                      </form>
                    )}

                    {/* Textarea */}
                    {currentStepData.type === 'textarea' && (
                      <div className="flex-1 flex flex-col">
                        <textarea
                          value={answers[currentStepData.id] || ''}
                          onChange={(e) => setAnswers({ ...answers, [currentStepData.id]: e.target.value })}
                          placeholder={currentStepData.placeholder}
                          className="w-full bg-transparent border-b-2 border-white/15 focus:border-[#FFC107] py-4 outline-none font-manrope text-white placeholder:text-white/20 resize-none transition-colors flex-1 min-h-[100px]"
                          autoFocus
                        />
                        <div className="flex items-center justify-between pt-6">
                          <button
                            type="button"
                            onClick={handleSkip}
                            className="font-manrope text-xs text-white/25 hover:text-white/50 transition-colors uppercase tracking-wider"
                          >
                            {t('colab.skip', 'Pular')}
                          </button>
                          <motion.button
                            onClick={handleFinalSubmit}
                            disabled={loading}
                            className="inline-flex items-center gap-2 h-12 px-8 bg-[#FFC107] text-black font-bold text-sm uppercase tracking-wider hover:bg-white transition-colors disabled:opacity-50 btn-spring"
                            whileHover={{ scale: 1.02 }}
                            whileTap={{ scale: 0.97 }}
                          >
                            {loading ? (
                              <motion.div
                                className="w-5 h-5 border-2 border-black/20 border-t-black rounded-full"
                                animate={{ rotate: 360 }}
                                transition={{ duration: 0.8, repeat: Infinity, ease: 'linear' }}
                              />
                            ) : (
                              <>
                                {t('colab.send', 'Enviar')} <Send size={16} />
                              </>
                            )}
                          </motion.button>
                        </div>
                      </div>
                    )}
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default ColabModal;
